'use client';

// Import necessary hooks and components
import React, { useState } from 'react';

import Link from 'next/link';

import AutoHeightImage from '@/components/cards/AutoHeightImage';
import { Card as CardType } from '@/types/Card';
import { Feature, FeatureType } from '@/types/Feature';

interface CardProps {
  card: CardType;
  numFeature?: number;
}

const featureKeys: Record<string, keyof Feature> = {
  '回饋內容：': 'feedback',
  '最高回饋：': 'max_feedback',
  '特殊條件：': 'condition',
  '適用通路：': 'channel',
};

// Helper function to get the appropriate styles for each feature type
const getFeatureTypeStyles = (type: FeatureType): string => {
  switch (type) {
    case FeatureType.CASH_BACK:
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
    case FeatureType.DISCOUNT:
      return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
    case FeatureType.FLIGHT:
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    case FeatureType.BENEFIT:
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    case FeatureType.MILEAGE:
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
    case FeatureType.AIRPORT_SERVICE:
      return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  }
};

// Helper function to format dates from YYYY-MM-DD to YYYY/MM/DD display format
const formatCardDate = (dateString: string): string => {
  // If the date is already in the correct format, just return it
  if (!dateString || dateString.includes('/')) return dateString;

  // Convert from YYYY-MM-DD to YYYY/MM/DD
  const [year, month, day] = dateString.split('-');
  return `${year}/${month}/${day}`;
};

const renderDetailValue = (
  value: string | string | Date | undefined
): string | string | undefined => {
  if (value instanceof Date) {
    return value.toLocaleDateString(); // Convert Date to string
  }
  return value; // Use string as is
};

// Helper function to get feature highlight text for the tooltip
// const getFeatureHighlight = (card: CardType): string => {
//   if (!card.features || card.features.length === 0) return '';

//   // Find the best cashback feature
//   const bestFeature = card.features.find(
//     f => f.type === FeatureType.CASH_BACK && f.feedback && f.feedback.includes('%')
//   );

//   if (bestFeature) {
//     return `${bestFeature.channel}: ${bestFeature.feedback}`;
//   }

//   // Fallback to the first feature
//   return `${card.features[0].channel}: ${card.features[0].feedback}`;
// };

const CreditCardIntro: React.FC<CardProps> = ({ card, numFeature = -1 }) => {
  const imageSrc = card.picture ? `/images/credit-cards/${card.picture}` : '';
  const [isOpen, setIsOpen] = useState<Record<string, boolean>>({});
  const [isHovered, setIsHovered] = useState(false);

  const toggleFeature = (key: string) => {
    setIsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden w-full group relative transform hover:scale-[1.01]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Info indicator */}
      <div className="absolute top-3 right-3 bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 shadow-md">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {/* Tooltip */}
      {/* {isHovered && (
        <div
          className="absolute bg-blue-600 text-white px-4 py-3 rounded-xl shadow-xl z-50 w-64 text-center"
          style={{
            top: '0',
            right: '0',
            transform: 'translate(10px, -10px)',
            pointerEvents: 'none', // Prevents the tooltip from interfering with clicks
          }}
        >
          <div className="font-bold mb-1">{card.name}</div>
          <div className="text-sm">{getFeatureHighlight(card)}</div>
          <div className="text-xs text-blue-100 mt-2 bg-blue-700/50 py-1 px-2 rounded-lg inline-block">
            點擊查看詳情
          </div>
        </div>
      )} */}

      {/* Card Info Link - covers most of the card except the bottom buttons */}
      <div
        onClick={() => (window.location.href = `/credit-cards/card/${card.key}`)}
        className="cursor-pointer"
      >
        {/* Card Header - Horizontal Layout */}
        <div className="flex flex-row items-center border-b border-gray-100 dark:border-gray-700 p-4">
          <div className="w-24 h-24 flex-shrink-0 flex items-center justify-center bg-white dark:bg-gray-700 rounded-lg p-2 shadow-sm">
            <AutoHeightImage
              src={imageSrc}
              alt={`${card.name} logo`}
              width={90}
              className="rounded-sm object-contain max-h-20"
              priority={false}
            />
          </div>
          <div className="flex-1 ml-4">
            <h2 className="font-bold text-gray-800 dark:text-white text-xl transition-colors duration-300">
              {card.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 transition-colors duration-300">
              {card.summary}
            </p>
            <div className="flex items-center mt-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-2">
                {card.issuing_bank}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                {card.payment_network}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Features - Single Column */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm mb-3 transition-colors duration-300">
          主要優惠
        </h3>
        <div className="space-y-3">
          {card.features?.slice(0, numFeature).map((feature, index) => (
            <div
              key={index}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border ${isOpen[`feature-${index}`] ? 'border-blue-200 dark:border-blue-800' : 'border-gray-100 dark:border-gray-700'} overflow-hidden transition-all duration-300 hover:shadow-md`}
            >
              <div
                className={`flex items-center justify-between cursor-pointer p-3 ${isOpen[`feature-${index}`] ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                onClick={() => toggleFeature(`feature-${index}`)}
              >
                <div className="flex items-center">
                  {/* Feature type indicator */}
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3 ${getFeatureTypeStyles(feature.type)}`}
                  >
                    {feature.type === FeatureType.CASH_BACK && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {feature.type === FeatureType.DISCOUNT && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {feature.type === FeatureType.BENEFIT && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    )}
                    {feature.type === FeatureType.FLIGHT && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11.43a1 1 0 00-.725-.962l-5-1.428a1 1 0 01.725-1.962l5 1.428a1 1 0 00.275.039h5.587c.483 0 .856-.002 1.142-.202.29-.202.475-.592.573-1.07.114-.546.092-1.214.057-2.053-.021-.502-.02-.988.033-1.418.055-.444.18-.863.535-1.216.35-.35.772-.478 1.218-.534.43-.054.915-.055 1.418-.033.838.035 1.506.057 2.053-.057.478-.098.868-.283 1.07-.574.2-.286.202-.659.202-1.142V3.143a1 1 0 00-1.106-.982l-5.587.569a1 1 0 00-.916 1.043V7.43a1 1 0 00.725.962l5 1.428a1 1 0 01-.725 1.962l-5-1.428a1 1 0 00-1.275.962v4.143a1 1 0 01-.716.962l-5 1.429a1 1 0 01-1.169-1.409l7-14z" />
                      </svg>
                    )}
                    {feature.type === FeatureType.MILEAGE && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 3.636a1 1 0 010 1.414 7 7 0 000 9.9 1 1 0 11-1.414 1.414 9 9 0 010-12.728 1 1 0 011.414 0zm9.9 0a1 1 0 011.414 0 9 9 0 010 12.728 1 1 0 11-1.414-1.414 7 7 0 000-9.9 1 1 0 010-1.414zM7.879 6.464a1 1 0 010 1.414 3 3 0 000 4.243 1 1 0 11-1.415 1.414 5 5 0 010-7.07 1 1 0 011.415 0zm4.242 0a1 1 0 011.415 0 5 5 0 010 7.072 1 1 0 01-1.415-1.415 3 3 0 000-4.242 1 1 0 010-1.415zM10 9a1 1 0 011 1v.01a1 1 0 11-2 0V10a1 1 0 011-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {feature.type === FeatureType.AIRPORT_SERVICE && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M4.36 4.09l.9 1.8h6.48l.9-1.8a.5.5 0 01.44-.28h1.04c.38 0 .63.42.44.75L13 7h1.75c.28 0 .5.22.5.5v1c0 .28-.22.5-.5.5H13l1.56 3.44a.5.5 0 01-.44.75h-1.04a.5.5 0 01-.44-.28L11.74 11H5.26l-.9 1.8a.5.5 0 01-.44.28H2.88a.5.5 0 01-.44-.75L4 9H2.25a.5.5 0 01-.5-.5v-1c0-.28.22-.5.5-.5H4L2.44 3.56a.5.5 0 01.44-.75h1.04c.2 0 .37.12.44.28z" />
                        <path d="M2 16a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1z" />
                      </svg>
                    )}
                    {feature.type === FeatureType.TRAVEL && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                        <path d="M3 4h3.5a1 1 0 01.8.4l4.2 5.6c.2.3.5.4.8.4H15c1.1 0 2 .9 2 2v1c0 1.1-.9 2-2 2h-1a1 1 0 01-1-1v-1h-6.1l-1.7-2.6c-.2-.3-.5-.4-.8-.4H3c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <path d="M5 6a1 1 0 100-2 1 1 0 000 2z" />
                      </svg>
                    )}
                    {feature.type === FeatureType.HOTEL && (
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M2 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H3a1 1 0 01-1-1V4z" />
                        <path d="M2 11a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H3a1 1 0 01-1-1v-4z" />
                        <path d="M11 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V4z" />
                        <path d="M11 11a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                    )}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-md font-bold text-gray-800 dark:text-white transition-colors duration-300">
                      {feature.feedback.split(' ')[0]}
                      <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">
                        {feature.feedback.split(' ').slice(1).join(' ')}
                      </span>
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                      {feature.simplified_condition}
                    </span>
                  </div>
                </div>

                <div className="flex-shrink-0 ml-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen[`feature-${index}`] ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}
                  >
                    <svg
                      className={`w-4 h-4 transition-transform duration-300 ${isOpen[`feature-${index}`] ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Expanded details */}
              <div
                className={`overflow-hidden transition-all duration-300 ${isOpen[`feature-${index}`] ? 'max-h-96' : 'max-h-0'}`}
              >
                <div className="p-4 bg-white dark:bg-gray-800">
                  <ul className="space-y-3">
                    {Object.entries(featureKeys).map(([key, featureKey]) => {
                      const detailValue = feature[featureKey];
                      if (!detailValue) return null;
                      return (
                        <li key={key} className="flex">
                          <span className="font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
                            {key}
                          </span>
                          <span className="text-gray-800 dark:text-gray-200 flex-1">
                            {renderDetailValue(detailValue as string)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Card Details */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center transition-all duration-300 hover:shadow-md">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-green-600 dark:text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              國內回饋
            </div>
            <div className="text-lg font-bold text-gray-800 dark:text-white">
              {card.domestic_feedback.includes('%')
                ? card.domestic_feedback.split('%')[0] + '%'
                : card.domestic_feedback}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center transition-all duration-300 hover:shadow-md">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              海外回饋
            </div>
            <div className="text-lg font-bold text-gray-800 dark:text-white">
              {card.international_feedback.includes('%')
                ? card.international_feedback.split('%')[0] + '%'
                : card.international_feedback}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-center transition-all duration-300 hover:shadow-md">
            <div className="flex items-center justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-amber-600 dark:text-amber-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              指定通路
            </div>
            <div className="text-lg font-bold text-gray-800 dark:text-white">
              {card.specific_channel_feedback.includes('%')
                ? card.specific_channel_feedback.split('%')[0] + '%'
                : card.specific_channel_feedback}
            </div>
          </div>
        </div>
      </div>

      {/* Card Footer */}
      <div className="bg-gray-50 dark:bg-gray-800 p-5 mt-2 border-t border-gray-100 dark:border-gray-700 transition-all duration-300">
        <div className="flex items-center mb-4">
          <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-2">
            <svg
              className="w-3 h-3 text-blue-600 dark:text-blue-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 font-medium transition-colors duration-300">
            {`活動日期：`}
            <span className="font-semibold text-gray-800 dark:text-white">
              {`${formatCardDate(card.start_date)} ~ ${formatCardDate(card.end_date)}`}
            </span>
          </div>
        </div>
        <div className="flex justify-between space-x-4">
          <Link
            href={card.link as string}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-white border border-gray-200 dark:border-gray-600 text-center px-4 py-3 rounded-xl font-medium text-sm flex-1 transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
            </svg>
            更多內容
          </Link>
          <Link
            href={card.apply_link as string}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white text-center px-4 py-3 rounded-xl font-medium text-sm flex-1 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                clipRule="evenodd"
              />
            </svg>
            手刀申請
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CreditCardIntro;
